import { describeApiError, type UnclaimedPlace } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Spinner } from '@brush/ui';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useAssignPlace, useCreatePlayer, useUnclaimedPlaces } from '../../queries';
import { emptyStateSentence, fileCardNote, placeLine, unclaimedCount } from '../../unclaimed/places';
import { PlayerModal } from './PlayerModal';
import { PlayerPicker } from './PlayerPicker';

/**
 * Every place in the room's results that nobody has claimed yet (ADR 0013), and the desk's way to
 * claim each one: pick the player the room already holds, or card a new one and assign them.
 *
 * <b>The main claiming route</b>, where the results editor is the secondary one: a person comes to
 * the desk about a finish, and this is the list the desk reads it from, across every tournament
 * rather than one at a time. Each claim goes through `PUT …/results/{position}/player`, which names
 * the row on screen — so a place a re-import replaced since this list loaded is refused rather than
 * claimed, and the list is refetched.
 *
 * Part of the Players section rather than a section of its own: a claim is always "which player
 * finished here", and the top navigation's width is the scarcest thing in this app.
 */
export function UnclaimedPlacesView({
  roomId,
  onBack,
}: {
  roomId: string;
  onBack: () => void;
}) {
  const placesQ = useUnclaimedPlaces(roomId);
  const assign = useAssignPlace(roomId);
  const createPlayer = useCreatePlayer(roomId);

  // The place whose row has its search open, and the card typed there when "not listed" was chosen.
  const [open, setOpen] = useState<string | null>(null);
  const [carding, setCarding] = useState<{ place: UnclaimedPlace; cardId: string } | null>(null);

  const claim = (place: UnclaimedPlace, playerId: string) =>
    assign.mutate(
      { tournamentId: place.tournamentId, position: place.position, resultId: place.resultId, playerId },
      { onSuccess: () => setOpen(null) },
    );

  if (placesQ.isLoading) return <Spinner label="Loading unclaimed places…" />;
  if (placesQ.isError) {
    return (
      <div className="p-8">
        <ErrorRetry message="Couldn't load unclaimed places." onRetry={() => void placesQ.refetch()} />
      </div>
    );
  }

  const places = placesQ.data ?? [];

  return (
    <div className="p-8">
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-sm text-ink-800/60 hover:text-ink-950"
      >
        <ArrowLeft className="size-4" /> Back to players
      </button>

      <div className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-ink-950">Unclaimed places</h1>
        <p className="mt-1 text-sm text-ink-800/60">
          Finishes the results record while nobody has claimed them.{' '}
          {places.length > 0 ? `${unclaimedCount(places.length)}.` : ''}
        </p>
      </div>

      <ErrorNotice message={describeApiError(assign.error)} className="mb-4" />

      {places.length === 0 ? (
        <p className="rounded-xl border border-ink-800/10 bg-white p-8 text-center text-sm text-ink-800/60 shadow-sm">
          {emptyStateSentence()}
        </p>
      ) : (
        <ul className="divide-y divide-ink-800/5 rounded-xl border border-ink-800/10 bg-white shadow-sm">
          {places.map((place) => {
            const note = fileCardNote(place);
            const isOpen = open === place.resultId;
            return (
              <li key={place.resultId} className="px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-ink-950">{placeLine(place)}</p>
                    {note ? <p className="mt-0.5 text-xs text-ink-800/55">{note}</p> : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : place.resultId)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-ink-800/15 px-3 py-1.5 text-sm text-ink-900 hover:border-felt-500"
                  >
                    <UserPlus className="size-4" /> {isOpen ? 'Cancel' : 'Assign a player'}
                  </button>
                </div>
                {isOpen ? (
                  <div className="mt-3">
                    <PlayerPicker
                      roomId={roomId}
                      placeholder="Who finished here — card ID, name, city…"
                      onPick={(p) => claim(place, p.id)}
                      onCreateNew={(term) => setCarding({ place, cardId: term })}
                    />
                    <p className="mt-1 text-[11px] text-ink-800/50">
                      Not listed? Choose “not listed” to card them with their own card first.
                    </p>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {carding ? (
        <PlayerModal
          roomId={roomId}
          initialCardId={carding.cardId}
          saving={createPlayer.isPending || assign.isPending}
          errorMessage={describeApiError(createPlayer.error)}
          // Carded through the ordinary player route — so a card equal to the room's marker for an
          // unclaimed place is refused here as anywhere — and then assigned to the place.
          onCreate={(body) =>
            createPlayer.mutate(body, {
              onSuccess: (player) => {
                claim(carding.place, player.id);
                setCarding(null);
              },
            })
          }
          onPatch={() => undefined}
          onClose={() => setCarding(null)}
        />
      ) : null}
    </div>
  );
}
